import { useEffect, useState } from "react";
import { Edit, Save, X, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // local editable copy

  const load = () => api.get("/categories").then(r => setCats(r.data || []));
  useEffect(() => { load(); }, []);

  const openEdit = (cat) => {
    setEditing({
      ...cat,
      subcategories: (cat.subcategories || []).map(s =>
        typeof s === "string" ? { slug: s, name: s } : { ...s }
      ),
    });
  };

  const updateField = (key, val) => setEditing(e => ({ ...e, [key]: val }));

  const updateSubcat = (idx, key, val) => {
    setEditing(e => {
      const subs = [...e.subcategories];
      subs[idx] = { ...subs[idx], [key]: val };
      return { ...e, subcategories: subs };
    });
  };

  const addSubcat = () => {
    setEditing(e => ({ ...e, subcategories: [...e.subcategories, { slug: "", name: "" }] }));
  };

  const removeSubcat = (idx) => {
    setEditing(e => ({ ...e, subcategories: e.subcategories.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    try {
      await api.put(`/admin/categories/${editing.slug}`, {
        name: editing.name,
        description: editing.description,
        image: editing.image,
        subcategories: editing.subcategories.filter(s => s.slug && s.name),
      });
      toast.success("Categoría guardada");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (slug) => {
    if (!window.confirm("¿Eliminar esta categoría? Solo es posible si no tiene productos.")) return;
    try {
      await api.delete(`/admin/categories/${slug}`);
      toast.success("Categoría eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-categories">
      <p className="overline mb-3">Catálogo · Taxonomía</p>
      <h1 className="font-heading text-4xl text-ivory mb-3">Categorías</h1>
      <p className="text-sm text-ivory/60 mb-8 max-w-2xl">Renombra categorías padre y modifica sus subcategorías. El slug (identificador interno) se mantiene fijo para no romper enlaces existentes.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
        {cats.map(c => (
          <div key={c.slug} className="bg-graphite border border-smoke overflow-hidden" data-testid={`cat-card-${c.slug}`}>
            <div className="relative h-40 overflow-hidden bg-smoke">
              {c.image && <img src={c.image} alt={c.name} className="w-full h-full object-cover opacity-70" />}
              <div className="absolute inset-0 bg-gradient-to-t from-graphite via-graphite/30 to-transparent" />
              <div className="absolute bottom-3 left-4 right-4">
                <p className="font-heading text-2xl text-ivory">{c.name}</p>
                <p className="text-xs text-ivory/50 font-mono">/{c.slug}</p>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-ivory/60 line-clamp-2">{c.description}</p>
              <div className="flex flex-wrap gap-1">
                {(c.subcategories || []).slice(0, 6).map((s, i) => (
                  <span key={i} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-smoke text-ivory/70">
                    {typeof s === "string" ? s : s.name}
                  </span>
                ))}
                {(c.subcategories || []).length > 6 && (
                  <span className="text-[10px] text-ivory/40 px-2 py-1">+{c.subcategories.length - 6}</span>
                )}
              </div>
              <div className="flex gap-2 pt-3 border-t border-smoke">
                <button onClick={() => openEdit(c)} data-testid={`edit-cat-${c.slug}`} className="flex-1 text-xs uppercase tracking-wider text-gold hover:text-ivory flex items-center justify-center gap-2 py-2 border border-gold/30 hover:border-gold transition-colors">
                  <Edit size={12} /> Editar
                </button>
                <button onClick={() => del(c.slug)} className="text-ivory/40 hover:text-rose px-3 border border-smoke hover:border-rose transition-colors" title="Eliminar"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-carbon/90 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-graphite border border-smoke max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-smoke flex justify-between">
              <h2 className="font-heading text-2xl text-ivory">Editar categoría</h2>
              <button onClick={() => setEditing(null)} className="text-ivory hover:text-gold"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="text-xs text-ivory/40 uppercase tracking-wider">Slug fijo: <span className="text-gold font-mono">{editing.slug}</span></div>
              <div>
                <label className="luxe-label">Nombre visible</label>
                <input data-testid="cat-edit-name" value={editing.name} onChange={e => updateField("name", e.target.value)} className="luxe-input" />
              </div>
              <div>
                <label className="luxe-label">Descripción</label>
                <input value={editing.description || ""} onChange={e => updateField("description", e.target.value)} className="luxe-input" />
              </div>
              <div>
                <label className="luxe-label">URL imagen</label>
                <input value={editing.image || ""} onChange={e => updateField("image", e.target.value)} className="luxe-input" />
                {editing.image && <img src={editing.image} alt="" className="mt-2 h-20 object-cover border border-smoke" />}
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="luxe-label !mb-0">Subcategorías</label>
                  <button onClick={addSubcat} className="text-xs text-gold uppercase tracking-wider flex items-center gap-1"><Plus size={12} /> Añadir</button>
                </div>
                <div className="space-y-2">
                  {editing.subcategories.map((s, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2 items-center">
                      <input
                        placeholder="slug-id (ej: perfumes-mujer)"
                        value={s.slug}
                        onChange={e => updateSubcat(i, "slug", e.target.value)}
                        className="luxe-input border-b border-smoke font-mono text-xs"
                      />
                      <div className="flex gap-1">
                        <input
                          placeholder="Nombre visible"
                          value={s.name}
                          onChange={e => updateSubcat(i, "name", e.target.value)}
                          className="luxe-input border-b border-smoke"
                        />
                        <button onClick={() => removeSubcat(i)} className="text-ivory/40 hover:text-rose"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                  {editing.subcategories.length === 0 && (
                    <p className="text-xs text-ivory/40">Sin subcategorías. Añade una pulsando arriba.</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-smoke">
              <button onClick={save} data-testid="cat-save" className="btn-gold flex items-center gap-2"><Save size={14} /> Guardar</button>
              <button onClick={() => setEditing(null)} className="btn-outline-gold">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
